import OnPlatePosition from "../components/plate/OnPlatePosition.js";

export default class PlateListCmp {

  #callbacks = {
    onPlateCellClick: [],
    onSelectionChange: [],
    onInjectionHover: [],
  };
  plateViewModels = [];
  /**
   * @type {VisualizationDataSelection}
   */
  #selectionManager;

  constructor(selectionManager) {
    this.#selectionManager = selectionManager;
  }

  addPlate(plateViewModel) {
    this.plateViewModels.push(plateViewModel);
    this.#setEventListeners(plateViewModel);
  }
  onPlateCellClick(cb) {
    this.#callbacks.onPlateCellClick.push(cb);
  }
  onSelectionChange(cb) {
    this.#callbacks.onSelectionChange.push(cb);
  }
  onInjectionHover(cb) {
    this.#callbacks.onInjectionHover.push(cb);
  }
  updateSelectionByLiteralCoordinates(literalCoordinates) {
    let paramNumber = 0;
    let selectedInjections = [];
    for (let model of this.plateViewModels) {
      const injectionsOnPlate = model.transformLiteralCoordinatesToInjectionIds(literalCoordinates[paramNumber++]);
      selectedInjections = selectedInjections.concat(injectionsOnPlate);
    }
    return new Map(selectedInjections.map(i => [i, null]));
  }
  setActiveInjection(injectionId){
    for (let model of this.plateViewModels){
      model.injectionLoaded( injectionId);
    }
  }

  /** @param {Map<string, {injection: InjectionJson, color: string}>} selectedInjections */
  setComparedInjections(selectedInjections){
    this.#selectionManager.setComparedInjections(selectedInjections);
  }

  updateCompareSelection(){
    for (let model of this.plateViewModels){
      model.toggleComparingHighlight(this.#selectionManager.getComparedInjections());
    }
  }

  getFirstInjectionId(){
    if (this.plateViewModels.length === 0)
      return null;
    let firstPlate = this.plateViewModels[0].getPlate(0);
    let pos = firstPlate.getRight(new OnPlatePosition(0, -1));
    return firstPlate.getInjection(pos).id;
  }

  getFirstHighlightedInjectionId() {
    let id = null, idx = 0;
    do {
      id = this.plateViewModels[idx++].getTopmostHighlightedInjectionId();
    } while (id === null && idx < this.plateViewModels.length)
    return id;
  }
  getAllInjectionIds(){
    return [].concat(...[...this.plateViewModels].map(m=>m.getAllInjectionIds()));
  }
  getAllInjectionIdsFromPlateWithInjection(injectionId){
    for (let plateModel of this.plateViewModels) {
      let plateInjections = plateModel.getAllInjectionIds();
      if (plateInjections.includes(injectionId)) return plateInjections;
    }
  }

  injectionLoaded(injectionId) {
    for (const plateViewModel of this.plateViewModels)
      plateViewModel.injectionLoaded(injectionId);
  }

  navigationKeyPressed(event) {
    for (const plateViewModel of this.plateViewModels) {
      if (plateViewModel.hasActiveCellInCurrentLevel()) {
        plateViewModel.navigationKeyPressed(event);
        break;
      }
    }
  }
  changePlateLevel(data) {
    for (const plateViewModel of this.plateViewModels) {
      if (plateViewModel.hasActiveCellInCurrentLevel()) {
        plateViewModel.changePlateLevel(data.direction);
      }
    }
  }

  /**
   * @param {{ sourcePlateId: string, direction: string, navigateToHighlighted: boolean }} navigationReq
   */
  redirectPlateNavigation(navigationReq) {
    const upward = isUpwardDirection(navigationReq.direction);
    const targetPlateId = getTargetPlateId(navigationReq.sourcePlateId, upward);
    if (!targetPlateId) return;
    // Depending on the trend (previous or next), we need to highlight either the first or the last well in the plate
    for (let model of this.plateViewModels) {
      if (model.getPlateId() === targetPlateId) {
        const currentPlate = model.getCurrentPlate();
        const {rows, cols} = currentPlate.meta;
        let pos;
        if (upward) {
          const startFrom = new OnPlatePosition(rows-1, cols);
          if (navigationReq.navigateToHighlighted)
            pos = model.getNextHighlightedCellPosition(startFrom, 'ArrowLeft');
          else
            pos = currentPlate.getLeft(startFrom)
        } else {
          const startFrom = new OnPlatePosition(0, -1);
          if (navigationReq.navigateToHighlighted)
            pos = model.getNextHighlightedCellPosition(startFrom, 'ArrowRight');
          else
            pos = currentPlate.getRight(startFrom)
        }
        if (pos){
          this.#callbacks.onPlateCellClick.forEach(cb => cb({
            plateId: targetPlateId,
            injectionId: currentPlate.getInjection(pos).id
          }));
        } else {
          navigationReq.sourcePlateId = targetPlateId;
          this.redirectPlateNavigation(navigationReq);
        }
      }
    }
  }

  /**
   * @param {PlateViewModel} plateViewModel
   */
  #setEventListeners(plateViewModel) {
    this.#selectionManager.addListener(plateViewModel);
    plateViewModel.onNavigateOutside((navigationRequest) => {
      this.redirectPlateNavigation(navigationRequest);
    });
    plateViewModel.onCellClick((data) => {
      if (!data.shiftKey) {
        for (const model of this.plateViewModels) {
          let pos = null;
          if (model.getPlateId() === data.plateId && model.opts.showActiveWell)
            pos = model.getCurrentPlate().getCoordinatesByInjectionId(data.injectionId);
          model.setActiveCell(pos);
        }
      }
      this.#callbacks.onPlateCellClick.forEach(cb => cb(data));
    });
    plateViewModel.onChangeSelection((data) => {
      this.#callbacks.onSelectionChange.forEach(cb => cb(data));
    });
    plateViewModel.onInjectionHover((injectionId, isHovered) => {
      this.#callbacks.onInjectionHover.forEach(cb => cb(injectionId, isHovered));
    });
  }
}

/**
 * @param {string} direction - should be one of [ArrowLeft, ArrowUp, ArrowRight, ArrowDown]
 * @returns {boolean} - true if direction points to the LEFT or to the UP, otherwise — false
 */
function isUpwardDirection(direction){
  return ['ArrowLeft', 'ArrowUp'].includes(direction);
}

/**
 * @param {string} sourcePlateId
 * @param {boolean} prev - search direction (true->up, false-down)
 * @returns {string|null} - closest plate-id depending on direction or null if no plate have found
 */
export function getTargetPlateId(sourcePlateId, prev) {
  const source = document.querySelector(`.plate-container[data-plate-id="${sourcePlateId}"]`);
  const target = prev ? source.previousElementSibling : source.nextElementSibling;
  return target ? target.dataset.plateId : null;
}

